import * as Styled from "./DetailsTab.styles";

import { Box, Stack, Typography } from "@mui/material";
import {
  getAmOrPm,
  getHourFromMilliseconds,
  getMinutesFromMilliseconds,
  getMonthAndDayFromMilliseconds,
  nthNumber,
} from "../../../../utils/utils";

import { Contact } from "../../../../components";
import DOMPurify from "dompurify";
import { Event } from "../../../../types/event";
import { GatherDefaultImage } from "../../../../assets";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { createSelector } from "@reduxjs/toolkit";
import theme from "../../../../theme/theme";
import { useSelector } from "react-redux";

const DetailsTab = () => {
  const eventLoading = useSelector(
    createSelector(
      (state: any) => {
        return state.liveEvent.loading;
      },
      (loading: boolean) => {
        return loading;
      }
    )
  );

  const event = useSelector(
    createSelector(
      (state: any) => {
        return state.liveEvent.data;
      },
      (event: Event) => {
        return event;
      }
    )
  );

  const officeLocationMapping: { [key: string]: string } = {
    Boston: "399 Boylston St #1000, Boston, MA 02116",
    Hartford: "185 Asylum St #3201, Hartford, CT 06103",
    "Boston & Hartford (New England)":
      "399 Boylston St #1000, Boston, MA 02116 (Boston Office) & 185 Asylum St #3201, Hartford, CT 06103 (Hartford Office)",
  };

  const generateEventLocationMap = (event: Event) => {
    if (event.format !== "Virtual") {
      let location = event.location;

      if (event.eventType === "On-site") {
        if (event.rooms.length > 0) {
          location = officeLocationMapping[event.office] || event.location;
        }
      }

      if (location) {
        return location
          .replace(/\(.*?\)/g, "")
          .split("&")
          .map((loc, index) => {
            const trimmedLoc = loc.trim();

            return (
              <Styled.MapContainer
                key={index}
                loading="lazy"
                src={`https://www.google.com/maps/embed/v1/place?key=${
                  process.env.REACT_APP_GOOGLE_API_KEY || ""
                }&q=${encodeURIComponent(trimmedLoc)}`}
              ></Styled.MapContainer>
            );
          });
      }
    }
    return null;
  };

  const formatEventTime = (startDateTime: number, endDateTime: number) => {
    return (
      <Styled.ModalBodyMB2 variant="modalBody">
        {`${getHourFromMilliseconds(startDateTime)}${
          getMinutesFromMilliseconds(startDateTime) !== "00"
            ? `:${getMinutesFromMilliseconds(startDateTime)}`
            : ""
        }`}
        <Styled.SuperScript>{getAmOrPm(startDateTime)}</Styled.SuperScript>
        {" - "}
        {`${getHourFromMilliseconds(endDateTime)}${
          getMinutesFromMilliseconds(endDateTime) !== "00"
            ? `:${getMinutesFromMilliseconds(endDateTime)}`
            : ""
        }`}
        <Styled.SuperScript>{getAmOrPm(endDateTime)}</Styled.SuperScript>
      </Styled.ModalBodyMB2>
    );
  };

  return (
    <Styled.PaddedContainer>
      {eventLoading || !event ? (
        <Stack
          direction="column"
          width="100%"
          height="100vh"
          style={{
            marginTop: theme.spacing(2),
          }}
        >
          <Stack
            direction={{ mobile: "column", desktop: "row" }}
            width="100%"
            height="100%"
            spacing={{ mobile: 2, desktop: 2 }}
          >
            <Styled.SkeletonContent variant="rectangular" />
            <Styled.SkeletonContent variant="rectangular" />
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction="column"
          width="100%"
          style={{
            height: "100%",
            marginTop: theme.spacing(2),
          }}
        >
          <Stack
            direction={{ mobile: "column", desktop: "row" }}
            width="100%"
            spacing={{ mobile: 0, desktop: 6 }}
          >
            <Styled.DetailsContainer>
              <Styled.HeroContainer>
                <Styled.HeroBackground
                  style={{
                    backgroundImage: `url("${
                      event.eventHeroS3Url || GatherDefaultImage
                    }")`,
                    filter: event.eventHeroS3Url
                      ? "blur(50px) brightness(.9)"
                      : "",
                  }}
                ></Styled.HeroBackground>
                <Styled.HeroImage
                  src={event.eventHeroS3Url || GatherDefaultImage}
                  alt={event.title}
                />
              </Styled.HeroContainer>
              <Box>
                <Typography
                  variant="modalSubtitle"
                  style={{
                    marginBottom: theme.spacing(1),
                  }}
                >
                  Details
                </Typography>
                <Styled.ModalBodyMB2 variant="modalBody">
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(event.description),
                    }}
                  />
                </Styled.ModalBodyMB2>
                <Stack direction={"row"} width="100%">
                  <Box style={{ width: "50%" }}>
                    <Typography
                      variant="subtitle2"
                      style={{
                        color: theme.colors.neutral.darkGray,
                      }}
                    >
                      Date
                    </Typography>
                    <Styled.ModalBodyMB2 variant="modalBody">
                      {getMonthAndDayFromMilliseconds(event.startDateTime)}
                      <Styled.SuperScript>
                        {nthNumber(event.startDateTime)}
                      </Styled.SuperScript>
                    </Styled.ModalBodyMB2>
                    <Typography
                      variant="subtitle2"
                      style={{
                        color: theme.colors.neutral.darkGray,
                      }}
                    >
                      Dress Code
                    </Typography>
                    <Styled.ModalBodyMB2 variant="modalBody">
                      {event.dressCode || "N/A"}
                    </Styled.ModalBodyMB2>
                    <Typography
                      variant="subtitle2"
                      style={{
                        color: theme.colors.neutral.darkGray,
                      }}
                    >
                      Format
                    </Typography>
                    <Styled.ModalBodyMB2 variant="modalBody">
                      {event.format}
                    </Styled.ModalBodyMB2>
                    <Typography
                      variant="subtitle2"
                      style={{ color: theme.colors.neutral.darkGray }}
                    >
                      Event Tag(s)
                    </Typography>
                    <Styled.ModalBodyMB2 variant="modalBody">
                      {event.eventTags.join(", ")}
                    </Styled.ModalBodyMB2>
                  </Box>
                  <Box style={{ width: "50%" }}>
                    <Typography
                      variant="subtitle2"
                      style={{ color: theme.colors.neutral.darkGray }}
                    >
                      Time
                    </Typography>
                    {formatEventTime(event.startDateTime, event.endDateTime)}
                    <Typography
                      variant="subtitle2"
                      style={{ color: theme.colors.neutral.darkGray }}
                    >
                      Cost
                    </Typography>
                    <Styled.ModalBodyMB2 variant="modalBody">
                      {event.admissionCost ? `$${event.admissionCost}` : "N/A"}
                    </Styled.ModalBodyMB2>
                    <Typography
                      variant="subtitle2"
                      style={{ color: theme.colors.neutral.darkGray }}
                    >
                      Office
                    </Typography>
                    <Styled.ModalBodyMB2 variant="modalBody">
                      {event.googleMapsLocationLink ? (
                        <a
                          href={event.googleMapsLocationLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: theme.colors.primary.slalomBlue }}
                        >
                          {event.office}
                        </a>
                      ) : (
                        event.office
                      )}
                    </Styled.ModalBodyMB2>
                    {event.teamsLink !== "" && (
                      <>
                        <Typography
                          variant="subtitle2"
                          style={{ color: theme.colors.neutral.darkGray }}
                        >
                          Teams Meeting Link
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            transition: "opacity 0.3s ease",
                            "&:hover": {
                              opacity: 0.7,
                            },
                          }}
                          onClick={() => window.open(event.teamsLink, "_blank")}
                        >
                          <MicrosoftIcon
                            color="primary"
                            sx={{ width: "14px", height: "14px" }}
                          />
                          <Typography
                            variant="modalBody"
                            style={{
                              marginLeft: theme.spacing(1),
                              marginRight: theme.spacing(1),
                              color: theme.colors.primary.slalomBlue,
                              textDecoration: "underline",
                            }}
                          >
                            Join In Teams
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Styled.DetailsContainer>
            <Styled.LocationContainer>
              <Typography
                variant="modalSubtitle"
                style={{
                  marginBottom: theme.spacing(2),
                }}
              >
                Location
              </Typography>
              {generateEventLocationMap(event)}
              <Typography
                variant="subtitle2"
                style={{
                  color: theme.colors.neutral.darkGray,
                }}
              >
                Event Location
              </Typography>
              <Typography
                variant="modalBody"
                style={{
                  marginBottom: theme.spacing(2),
                }}
              >
                {event.format === "Virtual" ? "Virtual" : event.location}
              </Typography>
              {event.format !== "Virtual" && (
                <Typography
                  variant="subtitle2"
                  style={{
                    color: theme.colors.neutral.darkGray,
                  }}
                >
                  Transportation & Parking Details
                </Typography>
              )}
              {event.format !== "Virtual" && (
                <Styled.ModalBodyMB2 variant="modalBody">
                  {event.transportationParkingDetails || "N/A"}
                </Styled.ModalBodyMB2>
              )}
              <Typography
                variant="modalSubtitle"
                style={{
                  marginBottom: theme.spacing(2),
                }}
              >
                Primary Contact(s)
              </Typography>
              {event.primaryContacts.map((name: string) => {
                return (
                  <Contact
                    size={24}
                    spacing={theme.spacing(1)}
                    variant="modalBody"
                    name={name}
                    key={name}
                  />
                );
              })}
            </Styled.LocationContainer>
          </Stack>
        </Stack>
      )}
    </Styled.PaddedContainer>
  );
};

export default DetailsTab;
